import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "components/mui";
import EmailEditor from "react-email-editor";
import { Base64 } from "js-base64";
import { EmailPreview } from "components/emailHistory";

/**
 * Merge tags kept in sync with DropEditor so [[variable]] placeholders survive the
 * offline render instead of being stripped by Unlayer.
 */
const PREVIEW_MERGE_TAGS = {
  first_name: { name: "First Name", value: "[[first_name]]", sample: "John" },
  last_name: { name: "Last Name", value: "[[last_name]]", sample: "Doe" },
  company: { name: "Company", value: "[[company]]", sample: "Stream Data Systems" },
};

/**
 * Resolve HTML that can be rendered directly (no Unlayer instance needed):
 * the stored rendered_html, or a text template's HTML body.
 */
function getDirectHtml(template) {
  if (!template) return "";
  if (template.rendered_html && String(template.rendered_html).trim()) {
    return template.rendered_html;
  }
  const editorType = template.editor_type || (template.template_content_drop ? "drop" : "text");
  if (
    editorType === "text" &&
    template.template_content_text &&
    String(template.template_content_text).trim()
  ) {
    return template.template_content_text;
  }
  return "";
}

/**
 * Renders a template's content as a visual preview.
 *
 * Templates saved with rendered HTML (rendered_html, or a text template's HTML)
 * render instantly in a sandboxed iframe. Legacy "drop" templates that predate
 * rendered_html only carry the Unlayer design JSON — which has no offline HTML
 * form — so a hidden Unlayer instance is spun up once to export their HTML.
 */
function TemplatePreview({ template }) {
  const directHtml = getDirectHtml(template);
  const dropJson = template?.template_content_drop;
  const needsUnlayer = !directHtml && Boolean(dropJson && String(dropJson).trim());

  const [renderedFromDrop, setRenderedFromDrop] = useState("");
  const [rendering, setRendering] = useState(needsUnlayer);
  const [renderError, setRenderError] = useState(false);

  // Reset when the selected template changes — keyed on the design too, so a
  // restore/edit of the SAME template re-renders instead of showing a stale preview.
  useEffect(() => {
    setRenderedFromDrop("");
    setRenderError(false);
    setRendering(needsUnlayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.template_id, dropJson]);

  const handleUnlayerReady = (unlayer) => {
    try {
      unlayer.setMergeTags(PREVIEW_MERGE_TAGS);
      const design = JSON.parse(Base64.decode(dropJson));
      unlayer.loadDesign(design);
      // Export after a short delay so the design has finished loading (mirrors DropEditor).
      setTimeout(() => {
        unlayer.exportHtml((data) => {
          setRenderedFromDrop(data?.html || "");
          setRendering(false);
        });
      }, 600);
    } catch (err) {
      console.error("[TemplatePreview] Failed to render drop design:", err);
      setRenderError(true);
      setRendering(false);
    }
  };

  if (directHtml) {
    return <EmailPreview htmlContent={directHtml} />;
  }

  if (needsUnlayer) {
    return (
      <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: 300 }}>
        {renderedFromDrop ? (
          <EmailPreview htmlContent={renderedFromDrop} />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              height: "100%",
              minHeight: 300,
            }}
          >
            {rendering && (
              <>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Rendering preview…
                </Typography>
              </>
            )}
            {renderError && (
              <Typography variant="body2" color="text.secondary" align="center">
                Preview unavailable — this template&apos;s design could not be rendered.
                Open it in the Email Sender to view it.
              </Typography>
            )}
          </Box>
        )}

        {/* Hidden Unlayer instance — only used to convert the saved design JSON to HTML. */}
        {!renderedFromDrop && !renderError && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              width: 0,
              height: 0,
              overflow: "hidden",
              opacity: 0,
              pointerEvents: "none",
            }}
          >
            <EmailEditor
              key={template?.template_id}
              onReady={handleUnlayerReady}
              options={{ safeHtml: false }}
              style={{ height: "1px" }}
            />
          </Box>
        )}
      </Box>
    );
  }

  // Nothing renderable.
  return <EmailPreview htmlContent="" />;
}

export default TemplatePreview;
